import React, {useEffect, useState} from 'react'
import {
  getTrialRemindersSetting,
  updateTrialRemindersSetting,
  TrialRemindersConfig,
} from '../accounts/core/_requests'

const EMPTY: TrialRemindersConfig = {
  enabled: false,
  email: {
    enabled: false,
    d3: {subject: '', body: ''},
    d2: {subject: '', body: ''},
    d1: {subject: '', body: ''},
  },
  whatsapp: {enabled: false, d3: '', d2: '', d1: ''},
  sms: {enabled: false, d3: '', d2: '', d1: ''},
}

const DAY_LABELS: {key: 'd3' | 'd2' | 'd1'; label: string}[] = [
  {key: 'd3', label: '3 ימים לפני סיום'},
  {key: 'd2', label: 'יומיים לפני סיום'},
  {key: 'd1', label: 'יום לפני סיום'},
]

const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
  overflow: 'hidden',
  marginBottom: '20px',
}
const headerStyle: React.CSSProperties = {
  padding: '16px 20px',
  background: '#f9fafb',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}
const labelStyle: React.CSSProperties = {fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block'}
const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #d1d5db',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: 14,
  marginBottom: 12,
}

const Toggle: React.FC<{checked: boolean; onChange: (v: boolean) => void}> = ({checked, onChange}) => (
  <label style={{position: 'relative', display: 'inline-block', width: 44, height: 24, cursor: 'pointer'}}>
    <input type='checkbox' checked={checked} onChange={(e) => onChange(e.target.checked)} style={{opacity: 0, width: 0, height: 0}} />
    <span
      style={{
        position: 'absolute',
        inset: 0,
        borderRadius: 999,
        transition: '0.2s',
        background: checked ? '#1877F2' : '#cbd5e1',
      }}
    />
    <span
      style={{
        position: 'absolute',
        top: 3,
        insetInlineStart: checked ? 23 : 3,
        width: 18,
        height: 18,
        background: '#fff',
        borderRadius: '50%',
        transition: '0.2s',
      }}
    />
  </label>
)

const TrialRemindersSettings: React.FC = () => {
  const [cfg, setCfg] = useState<TrialRemindersConfig>(EMPTY)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    getTrialRemindersSetting()
      .then((res) => setCfg({...EMPTY, ...res.data}))
      .catch((e) => console.error('Failed to load trial reminders:', e))
      .finally(() => setLoading(false))
  }, [])

  const save = async () => {
    setSaving(true)
    setSaved(false)
    try {
      await updateTrialRemindersSetting(cfg)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      console.error('Failed to save trial reminders:', e)
      alert('שגיאה בשמירה')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return null

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <div style={{fontSize: 16, fontWeight: 700, color: '#1f2937'}}>
          <i className='bi bi-bell' style={{marginLeft: 8, color: '#1877F2'}}></i>
          הודעות תזכורת לתשלום (סיום ניסיון)
        </div>
        <Toggle checked={cfg.enabled} onChange={(v) => setCfg({...cfg, enabled: v})} />
      </div>

      <div style={{padding: '18px 20px'}}>
        <div
          style={{
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: 12.5,
            color: '#1e40af',
            marginBottom: 18,
          }}
        >
          ⚠️ שמירת ההודעות בלבד — כרגע לא נשלחות הודעות לאף לקוח. מיועד ללקוחות עם מעל 800 פוסטים שתקופת הניסיון שלהם מסתיימת.
          <br />
          ניתן להשתמש במשתנים: <code>{'{first_name}'}</code> <code>{'{login_link}'}</code> <code>{'{amount}'}</code> <code>{'{days}'}</code> <code>{'{date}'}</code>
        </div>

        {/* EMAIL */}
        <ChannelHeader
          icon='bi-envelope'
          title='אימייל'
          checked={cfg.email.enabled}
          onChange={(v) => setCfg({...cfg, email: {...cfg.email, enabled: v}})}
        />
        {DAY_LABELS.map(({key, label}) => (
          <div key={`email-${key}`} style={{marginBottom: 14}}>
            <span style={labelStyle}>{label}</span>
            <input
              style={inputStyle}
              placeholder='נושא המייל'
              value={cfg.email[key].subject}
              onChange={(e) =>
                setCfg({...cfg, email: {...cfg.email, [key]: {...cfg.email[key], subject: e.target.value}}})
              }
            />
            <textarea
              style={{...inputStyle, minHeight: 90, marginBottom: 0}}
              placeholder='תוכן המייל'
              value={cfg.email[key].body}
              onChange={(e) =>
                setCfg({...cfg, email: {...cfg.email, [key]: {...cfg.email[key], body: e.target.value}}})
              }
            />
          </div>
        ))}

        {/* WHATSAPP */}
        <div style={{height: 1, background: '#f1f5f9', margin: '18px 0'}} />
        <ChannelHeader
          icon='bi-whatsapp'
          title='וואטסאפ'
          checked={cfg.whatsapp.enabled}
          onChange={(v) => setCfg({...cfg, whatsapp: {...cfg.whatsapp, enabled: v}})}
        />
        {DAY_LABELS.map(({key, label}) => (
          <div key={`wa-${key}`} style={{marginBottom: 12}}>
            <span style={labelStyle}>{label}</span>
            <textarea
              style={{...inputStyle, minHeight: 70, marginBottom: 0}}
              placeholder='הודעת וואטסאפ'
              value={cfg.whatsapp[key]}
              onChange={(e) => setCfg({...cfg, whatsapp: {...cfg.whatsapp, [key]: e.target.value}})}
            />
          </div>
        ))}

        {/* SMS */}
        <div style={{height: 1, background: '#f1f5f9', margin: '18px 0'}} />
        <ChannelHeader
          icon='bi-chat-dots'
          title='SMS'
          checked={cfg.sms.enabled}
          onChange={(v) => setCfg({...cfg, sms: {...cfg.sms, enabled: v}})}
        />
        {DAY_LABELS.map(({key, label}) => (
          <div key={`sms-${key}`} style={{marginBottom: 12}}>
            <span style={labelStyle}>{label}</span>
            <textarea
              style={{...inputStyle, minHeight: 55, marginBottom: 0}}
              placeholder='הודעת SMS (קצרה)'
              value={cfg.sms[key]}
              onChange={(e) => setCfg({...cfg, sms: {...cfg.sms, [key]: e.target.value}})}
            />
          </div>
        ))}

        <div style={{display: 'flex', alignItems: 'center', gap: 12, marginTop: 20}}>
          <button
            onClick={save}
            disabled={saving}
            style={{
              background: '#1877F2',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '10px 22px',
              fontSize: 14,
              fontWeight: 600,
              cursor: saving ? 'default' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}
          >
            {saving ? 'שומר...' : 'שמירת הודעות'}
          </button>
          {saved && <span style={{color: '#16a34a', fontSize: 13, fontWeight: 600}}>✓ נשמר</span>}
        </div>
      </div>
    </div>
  )
}

const ChannelHeader: React.FC<{icon: string; title: string; checked: boolean; onChange: (v: boolean) => void}> = ({
  icon,
  title,
  checked,
  onChange,
}) => (
  <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12}}>
    <div style={{fontSize: 14, fontWeight: 700, color: '#1f2937'}}>
      <i className={`bi ${icon}`} style={{marginLeft: 8, color: '#1877F2'}}></i>
      {title}
    </div>
    <Toggle checked={checked} onChange={onChange} />
  </div>
)

export default TrialRemindersSettings
